import React from "react";
import { Flower2, Armchair, Target, Moon } from "lucide-react";

const IMG = "/images/jardin";

const Picture = ({ base, alt, className, sizes = "(max-width: 768px) 100vw, 50vw" }) => (
  <picture>
    <source srcSet={`${IMG}/${base}.webp`} type="image/webp" />
    <img
      src={`${IMG}/${base}.jpg`}
      alt={alt}
      loading="lazy"
      decoding="async"
      sizes={sizes}
      className={className}
    />
  </picture>
);

const experiences = [
  {
    base: "rosiers-roses",
    icon: Flower2,
    title: "Des roses à chaque détour",
    text: "Rosiers roses et jaunes, massifs fleuris et végétation généreuse rythment le jardin au fil des saisons.",
  },
  {
    base: "hamac-poules",
    icon: Armchair,
    title: "Prendre le temps",
    text: "Une sieste dans le hamac, les poules qui passent, le chant des oiseaux : ici, on ralentit vraiment.",
  },
  {
    base: "petanque-jardin",
    icon: Target,
    title: "Jouer à la pétanque",
    text: "Un vrai terrain ombragé pour des parties en famille ou entre amis, les après-midi d'été.",
  },
  {
    base: "grande-terrasse-jour",
    icon: Armchair,
    title: "Déjeuner dehors",
    text: "Une grande terrasse et un salon de jardin pour les repas au soleil et les longues soirées.",
  },
];

const GardenSection = () => {
  return (
    <section
      id="jardin"
      className="section-padding bg-white"
      aria-labelledby="garden-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#1C3A27]/70 bg-[#EDE6DB]/50 px-4 py-1.5 rounded-full mb-4">
            <Flower2 size={16} /> Le jardin
          </span>
          <h2
            id="garden-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Le jardin, un art de vivre
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Au-delà des murs, le jardin est un véritable espace de vie. Un lieu pour respirer,
            retrouver la nature et profiter du calme de la Suisse Normande.
          </p>
        </div>

        {/* Mosaïque éditoriale */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* Grande image gauche */}
          <div className="md:col-span-7 relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3] group">
            <Picture
              base={experiences[1].base}
              alt="Hamac installé entre les arbres et poules dans le jardin arboré du gîte"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, 58vw"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 className="font-serif text-2xl font-bold mb-1">Prendre le temps</h3>
              <p className="text-white/90 text-sm max-w-md">
                Une sieste dans le hamac, les poules qui passent, le calme retrouvé.
              </p>
            </div>
          </div>

          {/* Deux images droite */}
          <div className="md:col-span-5 grid grid-rows-2 gap-4 md:gap-6">
            <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[16/9] md:aspect-auto group">
              <Picture
                base="rosiers-roses"
                alt="Rosiers roses grimpants en fleurs dans le jardin"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 40vw"
              />
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[16/9] md:aspect-auto group">
              <Picture
                base="petanque-jardin"
                alt="Terrain de pétanque ombragé dans le jardin arboré"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 40vw"
              />
            </div>
          </div>
        </div>

        {/* Petites cartes d'expérience */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-10">
          {experiences.map((exp, i) => {
            const Icon = exp.icon;
            return (
              <div
                key={i}
                className="bg-[#EDE6DB]/30 rounded-xl p-5 border border-[#EDE6DB]/50"
              >
                <div className="w-10 h-10 rounded-full bg-[#1C3A27]/10 flex items-center justify-center mb-3">
                  <Icon size={20} className="text-[#1C3A27]" />
                </div>
                <h3 className="font-semibold text-[#1C3A27] mb-1">{exp.title}</h3>
                <p className="text-sm text-[#1C3A27]/70 leading-relaxed">{exp.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Respiration pleine largeur — ambiance nocturne premium */}
      <div className="relative mt-16 md:mt-20 h-[60vh] min-h-[420px] w-full overflow-hidden">
        <Picture
          base="terrasse-nuit-guirlandes"
          alt="Terrasse du gîte illuminée par des guirlandes lumineuses à la tombée de la nuit"
          className="w-full h-full object-cover"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <Moon size={32} className="text-white/90 mb-4" />
          <h3 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4 max-w-3xl">
            Terminer la journée sous les guirlandes
          </h3>
          <p className="text-white/90 text-lg max-w-xl">
            Quand le soir tombe, la terrasse s'illumine. Le moment idéal pour prolonger la soirée
            en famille ou entre amis.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GardenSection;
